/**
 * The sidebar's lengths grow with the window's height (Estevan 10-15: "compress the sidebar contents just
 * enough so that the user doesn't have to scroll vertically to see everything. The visual compression
 * went too far."). A short window gets the compact sidebar, which fits a 700 px window under the 80 px
 * header with every item and Invite people in view; each length then grows linearly with the window
 * until it reaches the roomy pre-team-invites look (40 px rows, 14 px text, 20 px icons, 20 px section
 * gaps, 40 px above the first section).
 *
 * The rule that keeps it from ever scrolling: at every window height the vertical lengths together grow
 * by at most 1 px per px of window (from 700 px the rows, gaps, Invite people and the space above the
 * first section share exactly 1 px per px; from 900 px, once they have stopped, that space grows on to
 * 40 px), so the room spare at 700 px is never used up by a taller window, nor left to pool under the
 * last item.
 * Measured live by scripts/layout_fixes_live_check.py at 700, 800 and 900.
 */
import type React from 'react';

/** One length: `min` up to a window `from` px tall, then `perPx` more per px of window height, up to `max`. */
export interface FitLength {
  min: number;
  from: number;
  perPx: number;
  max: number;
}

export const FIT_BASE_HEIGHT = 700;

/** The lengths that stack vertically, with how many times each stacks in the sidebar. */
export const FIT_VERTICAL = {
  row: { length: { min: 30, from: 700, perPx: 0.05, max: 40 }, count: 14 }, // every nav item
  sectionGap: { length: { min: 6, from: 700, perPx: 0.07, max: 20 }, count: 3 }, // between the four sections
  invite: { length: { min: 34, from: 700, perPx: 0.03, max: 40 }, count: 1 }, // the Invite people button
  padTop: { length: { min: 8, from: 700, perPx: 0.06, max: 20 }, count: 1 }, // above the first section ...
  padTopTall: { length: { min: 0, from: 900, perPx: 0.9, max: 20 }, count: 1 }, // ... plus this, when tall
} as const satisfies Record<string, { length: FitLength; count: number }>;

/** Lengths inside a row, which never change its height. */
export const FIT_INLINE = {
  text: { min: 13.5, from: 700, perPx: 0.0025, max: 14 },
  icon: { min: 16, from: 700, perPx: 0.02, max: 20 },
  label: { min: 11, from: 700, perPx: 0.005, max: 12 },
} as const satisfies Record<string, FitLength>;

/** The length at a window `height` px tall, as the CSS below computes it. */
export function fitLength(spec: FitLength, height: number): number {
  return Math.min(spec.max, Math.max(spec.min, spec.min + (height - spec.from) * spec.perPx));
}

function fitCss(spec: FitLength): string {
  return `clamp(${spec.min}px, calc(${spec.min}px + (100vh - ${spec.from}px) * ${spec.perPx}), ${spec.max}px)`;
}

/** The custom properties the sidebar's classes read (`h-[var(--sb-row)]` and the rest). */
export const SIDEBAR_FIT_STYLE = {
  '--sb-row': fitCss(FIT_VERTICAL.row.length),
  '--sb-section-gap': fitCss(FIT_VERTICAL.sectionGap.length),
  '--sb-pad-top': `calc(${fitCss(FIT_VERTICAL.padTop.length)} + ${fitCss(FIT_VERTICAL.padTopTall.length)})`,
  '--sb-invite': fitCss(FIT_VERTICAL.invite.length),
  '--sb-text': fitCss(FIT_INLINE.text),
  '--sb-icon': fitCss(FIT_INLINE.icon),
  '--sb-label': fitCss(FIT_INLINE.label),
} as React.CSSProperties;
